import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { statusLabel } from '../EstimateStatusSelect';
import { viewKey, marginTone } from './MarginViewSwitcher';
import { MarginSparkline } from './MarginSparkline';
import { rowMoney } from './CurrencyMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => `A$${Math.round(v || 0).toLocaleString()}`;
const pct = (v) => (v != null ? `${v}%` : '—');

/** Margin by region for the active view (implementation / product / combined), with an 8-week trend sparkline per region. */
export function MarginRegionCard({ view, regions }) {
  const [trend, setTrend] = useState({});
  const scope = view.id;
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/region-trend`, { params: { weeks: 8, scope }, withCredentials: true })
      .then((r) => setTrend(Object.fromEntries((r.data.regions || []).map((t) => [t.region === '—' ? 'Unassigned' : t.region, { ...t, weeks: r.data.weeks }]))))
      .catch(() => setTrend({}));
  }, [scope]);
  const trendTitle = (t) => t?.points ? t.weeks.map((w, i) => `${w}: ${t.points[i] == null ? '—' : `${t.points[i].toFixed(1)}%`} (${t.deals[i]} deal${t.deals[i] === 1 ? '' : 's'})`).join('\n') : '';
  const rows = (regions || []).filter((r) => (view.id === 'product' ? r.product_count > 0 : true));
  if (!rows.length) return null;
  return (
    <Card className="border-slate-200" data-testid="margin-by-region">
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold text-[#0F2040] mb-0.5">{view.label} margin by region</h3>
        <p className="text-[11px] text-slate-400 mb-2">Sparkline = weekly {view.label.toLowerCase()} margin % over the last 8 weeks, rebuilt from each deal's version history.</p>
        <div className="flex flex-wrap gap-2">
          {rows.map((r) => {
            const m = r[viewKey(view, 'margin_aud')]; const p = r[viewKey(view, 'margin_pct')];
            const t = trend[r.region];
            return (
              <div key={r.region} className="border border-slate-200 rounded-lg px-3 py-2 min-w-[170px]" data-testid={`margin-region-${r.region.toLowerCase()}`}
                title={t?.points ? `8-week ${view.label.toLowerCase()} margin trend\n${trendTitle(t)}` : undefined}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{r.region} · {view.id === 'product' ? r.product_count : r.count}</div>
                <div className={`text-sm font-bold ${m < 0 ? 'text-rose-600' : 'text-[#0F2040]'}`}>{fmtA(m)}
                  <span className={`ml-1 text-xs font-semibold ${(p ?? 0) < 20 ? 'text-amber-600' : 'text-emerald-700'}`}>{pct(p)}</span>
                </div>
                <div className="text-[10px] text-slate-400">Sell {fmtA(r[viewKey(view, 'sell_aud')])} · Cost {fmtA(r[viewKey(view, 'cost_aud')])}</div>
                {t && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px]" data-testid={`margin-region-trend-${scope}-${r.region}`}>
                    <MarginSparkline points={t.points} trend={t.trend} testId={`margin-region-sparkline-${scope}-${r.region}`} />
                    {t.delta_pts != null ? (
                      <span className={`tabular-nums ${t.trend === 'down' ? 'text-rose-600 font-semibold' : t.trend === 'up' ? 'text-emerald-700' : 'text-slate-400'}`} data-testid={`margin-region-trend-delta-${scope}-${r.region}`}>
                        {t.delta_pts > 0 ? '+' : ''}{t.delta_pts.toFixed(1)} pts · 8w
                      </span>
                    ) : <span className="text-slate-400">8w</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const EXTRA_COLS = {
  combined: [
    { h: 'Impl %', cell: (r) => <span className={marginTone(r.margin_pct)}>{pct(r.margin_pct)}</span>, tid: 'impl' },
    { h: 'Product %', cell: (r) => (r.has_product ? <span className={marginTone(r.product_margin_pct)}>{pct(r.product_margin_pct)}</span> : <span className="text-slate-300">—</span>), tid: 'product' },
  ],
  implementation: [
    { h: 'Product (A$)', cell: (r) => (r.has_product ? <span title={`${r.product_term_years}-yr subscription · cost ${fmtA(r.product_cost_aud)}`}>{fmtA(r.product_sell_aud)}</span> : <span className="text-slate-300">—</span>), tid: 'product' },
    { h: 'Combined %', cell: (r) => (r.has_product ? <span className={marginTone(r.total_margin_pct)}>{pct(r.total_margin_pct)}</span> : <span className="text-slate-300">—</span>), tid: 'combined' },
  ],
  product: [
    { h: 'Package', cell: (r) => <span className="text-slate-600 whitespace-nowrap">{r.product_package || '—'} · {r.product_term_years}-yr</span>, tid: 'package', left: true },
    { h: 'Impl %', cell: (r) => <span className={marginTone(r.margin_pct)}>{pct(r.margin_pct)}</span>, tid: 'impl' },
  ],
};

/** Margin by estimate — lowest first for the active view; rows sorted by that view's margin %. */
export function MarginByEstimateTable({ view, rows, minPct, filtersSlot, onOpenDeal , ccyMode = 'aud' }) {
  const rowFx = (r) => (view.id === 'implementation' || (view.id === 'combined' && !r.has_product) ? [r.currency_code || 'AUD', r.fx_impl_to_aud] : [r.product_currency || 'AUD', r.fx_product_to_aud]);
  const fmRow = (r) => rowMoney(ccyMode, ...rowFx(r));
  const sk = viewKey(view, 'margin_pct');
  const sorted = [...rows].sort((a, b) => (a[sk] == null) - (b[sk] == null) || (a[sk] ?? 0) - (b[sk] ?? 0));
  const extras = EXTRA_COLS[view.id] || [];
  return (
    <Card className="border-slate-200" data-testid="margin-by-estimate-card">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-[#0F2040] mb-1">{view.label} margin by estimate — lowest first</h3>
            <p className="text-[11px] text-slate-400">{view.id === 'product' ? 'Only estimates with a saved product subscription. ' : ''}All values converted to AUD via each estimate's FX snapshot. Leadership only. Click a row for the deal summary.</p>
          </div>
          {filtersSlot}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="margin-by-estimate-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-1.5 pr-2">Estimate</th><th className="py-1.5 pr-2">Client</th><th className="py-1.5 pr-2">Rep</th><th className="py-1.5 pr-2">Lifecycle</th>
                <th className="py-1.5 pr-2">Region</th><th className="py-1.5 pr-2">Ccy</th>
                <th className="py-1.5 pr-2 text-right">Sell{ccyMode === 'native' ? '' : ' (A$)'}</th><th className="py-1.5 pr-2 text-right">Cost{ccyMode === 'native' ? '' : ' (A$)'}</th>
                <th className="py-1.5 pr-2 text-right">Margin{ccyMode === 'native' ? '' : ' (A$)'}</th><th className="py-1.5 pr-2 text-right">Margin %</th>
                {extras.map((c) => <th key={c.h} className={`py-1.5 pr-2 ${c.left ? '' : 'text-right'}`}>{c.h}</th>)}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={10 + extras.length} className="py-4 text-center text-slate-400" data-testid="margin-by-estimate-empty">{view.id === 'product' ? 'No estimates with a saved product subscription match these filters.' : 'No estimates match these filters.'}</td></tr>
              )}
              {sorted.slice(0, 50).map((r) => {
                const mp = r[sk];
                const below = minPct != null && mp != null && mp < minPct;
                return (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${below ? 'bg-rose-50/60' : ''}`} onClick={() => onOpenDeal(r.id)} title="Open deal summary" data-testid={`margin-row-${r.id}`}>
                    <td className="py-1.5 pr-2 font-semibold text-[#0F2040] max-w-[240px] truncate">{r.name}</td>
                    <td className="py-1.5 pr-2 text-slate-600">{r.organization_name || '—'}</td>
                    <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap" data-testid={`margin-row-rep-${r.id}`}>{r.rep_name || <span className="text-slate-300">Unassigned</span>}</td>
                    <td className="py-1.5 pr-2 text-slate-500 whitespace-nowrap">{statusLabel(String(r.status || '').toLowerCase())}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{r.region || '—'}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{ccyMode === 'native' ? rowFx(r)[0] : (view.id === 'product' ? (r.product_currency || 'AUD') : (r.currency_code || 'AUD'))}</td>
                    <td className="py-1.5 pr-2 text-right" data-testid={`margin-row-sell-${r.id}`}>{fmRow(r)(r[viewKey(view, 'sell_aud')])}</td>
                    <td className="py-1.5 pr-2 text-right">{fmRow(r)(r[viewKey(view, 'cost_aud')])}</td>
                    <td className={`py-1.5 pr-2 text-right font-semibold ${r[viewKey(view, 'margin_aud')] < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmRow(r)(r[viewKey(view, 'margin_aud')])}</td>
                    <td className={`py-1.5 pr-2 text-right font-semibold ${marginTone(mp)}`} data-testid={`margin-row-pct-${r.id}`}>{pct(mp)}</td>
                    {extras.map((c) => <td key={c.h} className={`py-1.5 pr-2 ${c.left ? '' : 'text-right'} font-semibold`} data-testid={`margin-row-${c.tid}-${r.id}`}>{c.cell(r)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
